import React from 'react';
import { Input, Pagination, Spin, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  CommunityPanel,
  EmptyState,
  FeedList,
  FeedPaginationSummary,
  FeedPaginationWrap,
  FilterActions,
  FilterHeader,
  SearchInputWrap,
} from './index.styled';
import { CommunityMessage, CommunityGroup, Folder } from './types';
import MessageCard from './MessageCard';
import { GroupTitle } from 'components/groupCard/groupCard.styled';

type Props = {
  loading: boolean;
  filteredMessages: CommunityMessage[];
  totalMessages: number;
  currentPage: number;
  pageSize: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  isTranslateEnabled: boolean;
  onToggleTranslate: () => void;
  folders: Folder[];
  messageFolderMap: Record<string, string>;
  assigningMessageId: string | null;
  groups: CommunityGroup[];
  selectedGroupId: string;
  onSelectGroup: (id: string) => void;
  onTogglePicker: (messageId: string) => void;
  onClosePicker: () => void;
  onAssignFolder: (messageId: string, folderId: string | null) => void;
  isLoggedIn: boolean;
  currentUserId?: string;
  onCommentsCountChange: (messageId: string, count: number) => void;
  selectedFolderId?: string;
};

export default function MessageFeed({
  loading,
  filteredMessages,
  totalMessages,
  currentPage,
  pageSize,
  searchValue,
  onSearchChange,
  onPageChange,
  isTranslateEnabled,
  onToggleTranslate,
  folders,
  messageFolderMap,
  assigningMessageId,
  groups,
  selectedGroupId,
  onSelectGroup,
  onTogglePicker,
  onClosePicker,
  onAssignFolder,
  isLoggedIn,
  currentUserId,
  onCommentsCountChange,
  selectedFolderId = 'all',
}: Props) {
  const hasActiveFilter = selectedFolderId !== 'all' || searchValue.trim().length > 0;
  const showFilteredEmptyState =
    !loading && filteredMessages.length === 0 && totalMessages > 0 && hasActiveFilter;
  const showNoResults = !loading && filteredMessages.length === 0 && !showFilteredEmptyState;

  return (
    <CommunityPanel>
      <FilterHeader>
        <FilterActions>
          {/* <TranslateToggleButton type="button" onClick={onToggleTranslate} title="Toggle translation">
            {isTranslateEnabled ? 'En' : 'Pr'}
            <SwapOutlined />
            {isTranslateEnabled ? 'Pr' : 'En'}
          </TranslateToggleButton> */}
          <SearchInputWrap>
            <Input
              allowClear
              placeholder="Search messages, senders, or groups"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </SearchInputWrap>
        </FilterActions>
      </FilterHeader>

      <Tabs
        activeKey={selectedGroupId || ''}
        onChange={(key) => onSelectGroup(String(key))}
        type="line"
        size="large"
        items={groups.map((group) => ({
          key: group.id,
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              {group.coverPhotoUrl && (
                <img
                  src={group.coverPhotoUrl}
                  alt={group.title}
                  style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                />
              )}
              <GroupTitle>{group.title}</GroupTitle>
            </span>
          ),
        }))}
      />

      {loading ? (
        <EmptyState>
          <Spin />
        </EmptyState>
      ) : showNoResults ? (
        <EmptyState>{groups.length > 1 && !groups.find(group => group.id === selectedGroupId)?.title.includes('Zoroastrian') && 'No mirrored Telegram messages match the current filters.'}</EmptyState>
      ) : (
        <>
          {showFilteredEmptyState && (
            <EmptyState>No messages in this folder on this page. Try another page.</EmptyState>
          )}
          {filteredMessages.length > 0 && (
          <FeedList>
            {filteredMessages.map((message) => (
              <MessageCard
                key={message.id}
                message={message}
                folders={folders}
                assignedFolderId={messageFolderMap[message.id]}
                isPickerOpen={assigningMessageId === message.id}
                onTogglePicker={() => onTogglePicker(message.id)}
                onClosePicker={onClosePicker}
                onAssignFolder={(folderId) => onAssignFolder(message.id, folderId)}
                isLoggedIn={isLoggedIn}
                currentUserId={currentUserId}
                onCommentsCountChange={onCommentsCountChange}
                isTranslateEnabled={isTranslateEnabled}
              />
            ))}
          </FeedList>
          )}

          <FeedPaginationWrap>
            <FeedPaginationSummary>
              Showing {filteredMessages.length} of {totalMessages} messages
            </FeedPaginationSummary>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalMessages}
              onChange={onPageChange}
              showSizeChanger={false}
            />
          </FeedPaginationWrap>
        </>
      )}
    </CommunityPanel>
  );
}
